import { useEffect, useRef, useState } from "react";
import { useContent } from "@/lib/ContentContext";
import { Pencil } from "lucide-react";

/**
 * Inline editable text wrapper.
 * - In view mode: renders `fallback` (or saved override).
 * - In edit mode: shows a dashed outline + pencil; click to edit inline.
 *
 * Props:
 *   k: unique content key
 *   fallback: original text (used when no override exists)
 *   as: element tag to render in view mode (default 'span')
 *   className: forwarded
 *   multiline: allow line breaks in editor
 */
export default function EditableText({
  k,
  fallback,
  as: Tag = "span",
  className = "",
  multiline = false,
  ...rest
}) {
  const { get, setDraft, editMode, isAdmin } = useContent();
  const [active, setActive] = useState(false);
  const inputRef = useRef(null);
  const value = get(k, fallback);

  useEffect(() => {
    if (active && inputRef.current) {
      inputRef.current.focus();
      // place caret at end
      const el = inputRef.current;
      if (el.setSelectionRange) {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    }
  }, [active]);

  // Public viewer — render plain text only
  if (!isAdmin || !editMode) {
    return (
      <Tag className={className} {...rest}>
        {value}
      </Tag>
    );
  }

  // Admin in edit mode
  if (active) {
    const sharedProps = {
      ref: inputRef,
      value,
      onChange: (e) => setDraft(k, e.target.value),
      onBlur: () => setActive(false),
      onKeyDown: (e) => {
        if (e.key === "Escape") setActive(false);
        if (!multiline && e.key === "Enter") {
          e.preventDefault();
          setActive(false);
        }
      },
      className: `${className} bg-[#B87CFF]/15 outline outline-2 outline-[#B87CFF] rounded-md px-1 -mx-1 w-full`,
      style: { font: "inherit", color: "inherit", letterSpacing: "inherit" },
      "data-testid": `edit-input-${k}`,
    };
    if (multiline) {
      return <textarea rows={Math.min(8, Math.max(2, value.split("\n").length + 1))} {...sharedProps} />;
    }
    return <input type="text" {...sharedProps} />;
  }

  return (
    <Tag
      onClick={() => setActive(true)}
      className={`${className} group/edit relative cursor-text rounded-md transition-all`}
      style={{
        outline: "1px dashed rgba(184, 124, 255, 0.55)",
        outlineOffset: "3px",
      }}
      data-testid={`editable-${k}`}
      data-editkey={k}
      {...rest}
    >
      {value}
      <span
        aria-hidden
        className="absolute -top-2 -right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#B87CFF] text-[#0B0A4A] shadow opacity-0 group-hover/edit:opacity-100 transition-opacity"
      >
        <Pencil className="w-3 h-3" strokeWidth={2.2} />
      </span>
    </Tag>
  );
}
